import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { RowNumberBadge } from './ImportRowBadge';
import { SegmentedControl, type SegmentOption } from './SegmentedControl';
import { compactFieldLabel, clientFieldError } from './importWizardHelpers';
import { cn } from '../../../lib/utils';

interface NeedsFixingRowCardProps {
  row: ImportBatchRow;
  /** Every column the campaign accepts, in field order — the inline editor
   *  shows one cell per field (not just the ones with errors) so the admin
   *  can see the whole row at a glance while fixing it. */
  expectedFields: ExpectedField[];
  /** Gates the username wrong-platform-link live check in clientFieldError,
   *  same as the BE — omit when the campaign's platform isn't known. */
  campaignPlatform?: string;
  busy: boolean;
  onResolve: (rowNumber: number, action: 'keep' | 'drop' | 'fixed', values?: Record<string, string>) => Promise<void>;
}

/** One row that needed a fix — inline "Fix here" editing (merged with the
 *  row's other untouched values, spec: fixing one field must not wipe the
 *  rest) or "Skip row" to drop it. Neither a skip nor a successful fix
 *  removes the row from this list: a skipped row stays visible with a
 *  "Skipped" badge, and a fixed row (no more validation errors, but
 *  sheetResolution 'fixed') stays visible with a "Fixed" badge — either way
 *  the admin can still hit "Fix here" to change their mind. A skip
 *  round-trips sheetResolution 'drop' -> 'fixed' server-side, same as any
 *  other fix. */
export function NeedsFixingRowCard({ row, expectedFields, campaignPlatform, busy, onResolve }: NeedsFixingRowCardProps) {
  const [editing, setEditing] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const skipped = row.sheetResolution === 'drop';
  // Successfully fixed: the row was edited and no longer has any validation
  // errors. Once every field is valid, none of them individually needs
  // fixing anymore — re-entering edit mode must make every field editable
  // (not just the ones that used to error), or "Fix here" would open an
  // editor with nothing actually editable in it. Named rowFixed (not
  // `fixed`) to avoid shadowing the per-field `fixed` used below for the
  // live red->green border check.
  const rowFixed = row.sheetResolution === 'fixed' && Object.keys(row.validationErrors).length === 0;

  // Computes each field's live editable-state and validation status, in
  // field order. Used both to render the field editor below and to gate
  // "Save fix" on whether ANY currently-rendered field still has a live
  // error (an admin must not be able to trigger a round-trip that's certain
  // to fail). Only called while the editor is open — this data is never
  // consumed otherwise, so a collapsed row (the common case in a list of
  // many rows) skips the per-field clientFieldError calls entirely.
  function computeFieldsInfo() {
    return expectedFields.map((field) => {
      const hasError = field.key in row.validationErrors;
      const value = draftValues[field.key] ?? row.values[field.key] ?? '';
      // Whether the field STARTED this edit session empty — from the row's
      // original value, not the live draft. Deriving "missing" from the live
      // value instead would flip editable to false the moment the admin typed
      // a first character into a field that began empty with no server error
      // (e.g. an untouched optional deliverable price cell), freezing the
      // input into a read-only div after one keystroke.
      const missing = !hasError && !(row.values[field.key] ?? '');
      // Re-editing an already-fixed row: nothing currently errors, so every
      // field must be editable, not just the ones that used to — otherwise
      // re-opening a fully-fixed row would show an editor where nothing can
      // actually be changed.
      const editable = hasError || missing || rowFixed;
      // Live re-check against the CURRENT draft as the admin types — not the
      // frozen server error — so a field that was invalid can visibly turn
      // green the moment it's actually fixed, instead of only finding out
      // after Save fix round-trips.
      const currentError = editable ? clientFieldError(field.key, value, campaignPlatform, field.required) : null;
      const fixed = hasError && !currentError;
      return { field, value, missing, editable, currentError, fixed };
    });
  }
  const fieldsInfo = editing ? computeFieldsInfo() : [];
  const hasLiveFieldError = fieldsInfo.some((f) => f.currentError !== null);

  // Fix here / Skip row as one segmented control at the top of the row. The
  // active segment reflects the row's state directly — Skip when skipped, Fix
  // while the inline editor is open — so no separate "Skipped" status pill is
  // needed (choosing Skip is shown by the segment itself). "Fix here" on a
  // skipped row re-opens the editor and un-skips it on save.
  const segmentOptions: SegmentOption[] = [
    { key: 'fix', label: rowFixed ? 'Edit' : 'Fix here' },
    { key: 'skip', label: 'Skip row' },
  ];
  const activeSegment = editing ? 'fix' : skipped ? 'skip' : '';
  const onSegment = (key: string) => {
    if (key === 'fix') {
      if (!editing) { setEditing(true); setDraftValues({}); }
      return;
    }
    setEditing(false);
    onResolve(row.rowNumber, 'drop').catch(() => {});
  };

  return (
    <div className={`rounded-xl border p-3 text-[12px] ${skipped ? 'border-border bg-muted/40' : rowFixed ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`}>
      <div className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
        <RowNumberBadge n={row.rowNumber} />
        {row.values.username && <span>{row.values.username}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {rowFixed && (
            <span className="inline-flex h-5 items-center rounded-full bg-emerald-50 px-2.5 text-caption-medium text-emerald-700">
              Fixed
            </span>
          )}
          <SegmentedControl
            ariaLabel={`Fix ${row.values.username ?? `row ${row.rowNumber}`}`}
            options={segmentOptions}
            value={activeSegment}
            onChange={onSegment}
            disabled={busy}
            size="xs"
          />
        </span>
      </div>
      {/* Once editing, the red-bordered field itself is what highlights the
          error — this text list would just repeat it, so it only shows in
          the collapsed summary view. */}
      {!editing && Object.entries(row.validationErrors).map(([field, message]) => (
        <div key={field} className={skipped ? 'text-muted-foreground' : 'text-rose-600'}>{field}: {message}</div>
      ))}

      {editing && (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {fieldsInfo.map(({ field, value, missing, editable, currentError, fixed }) => {
              return (
                <div key={field.key} className="flex min-w-[130px] flex-1 flex-col gap-1">
                  <label
                    htmlFor={`fix-${row.rowNumber}-${field.key}`}
                    className="truncate text-[10px] tracking-wide text-muted-foreground uppercase"
                  >
                    {compactFieldLabel(field)}
                  </label>
                  {editable ? (
                    <>
                      <input
                        id={`fix-${row.rowNumber}-${field.key}`}
                        className={cn(
                          'h-[30px] rounded-md border bg-card px-2 text-[12px] text-foreground outline-none transition-colors duration-150',
                          currentError
                            ? 'border-2 border-rose-600 ring-2 ring-rose-200 focus-visible:ring-rose-300'
                            : fixed
                              ? 'border-emerald-400 ring-1 ring-emerald-200 focus-visible:ring-emerald-300'
                              : 'border-border focus-visible:ring-2 focus-visible:ring-primary/20',
                        )}
                        placeholder={missing ? '—' : undefined}
                        value={value}
                        disabled={busy}
                        aria-invalid={Boolean(currentError)}
                        aria-describedby={currentError ? `fix-${row.rowNumber}-${field.key}-error` : undefined}
                        onChange={(e) => setDraftValues((d) => ({ ...d, [field.key]: e.target.value }))}
                      />
                      {/* Color alone (the red border) isn't an accessible error
                          signal — this is the same live re-check surfaced as
                          visible, screen-reader-reachable text. */}
                      {currentError && (
                        <p id={`fix-${row.rowNumber}-${field.key}-error`} className="text-[10px] text-rose-600">
                          {currentError}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex h-[30px] items-center truncate rounded-md bg-muted px-2 text-[12px] text-foreground">
                      {value}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy || hasLiveFieldError}
              onClick={() => {
                onResolve(row.rowNumber, 'fixed', { ...row.values, ...draftValues })
                  .then(() => {
                    setEditing(false);
                    setDraftValues({});
                  })
                  .catch(() => {});
              }}
            >
              Save fix
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
